import React, { ReactNode, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Bold from 'icons/Bold.icon';
import Italic from 'icons/Italic.icon';
import UnorderedList from 'icons/UnorderedList.icon';
import OrderedList from 'icons/OrderedList.icon';
import Underline from 'icons/Underline.icon';
import EditorActions from '../../utils/EditorActions';
import { useSlate } from 'slate-react';
import { EditorToolbarActions } from '../../models/RichTextEditor';
import {
  ELEMENT_TYPES_ARRAY,
  TEXT_INDENT_ARRAY,
  TEXT_STYLES_ARRAY,
} from '../../constants';
import { ElementTypes, TextIndent } from '../../models/EditorElements';
import EditorActionButton from '../EditorActionButton';
import { TextStyle } from '../../models/EditorText';

import './RichTextToolbar.styles.scss';

type RichTextToolbarProps = {
  className?: string;
  readOnly: boolean;
  additionalAction?: ReactNode;
};

const RichTextToolbar: React.FC<RichTextToolbarProps> = (props) => {
  const { className, readOnly, additionalAction } = props;

  const editor = useSlate();

  const classes = classNames(
    'anys-rich-text-toolbar',
    { 'anys-rich-text-toolbar--read-only': readOnly },
    className,
  );

  const checkIfActionActive = useCallback(
    (actionName: EditorToolbarActions) => {
      if (TEXT_STYLES_ARRAY.includes(actionName as TextStyle))
        return EditorActions.isMarkActive(editor, actionName as TextStyle);

      if (TEXT_INDENT_ARRAY.includes(actionName as TextIndent)) return false;

      if (ELEMENT_TYPES_ARRAY.includes(actionName as ElementTypes))
        return EditorActions.isBlockActive(editor, actionName as ElementTypes);
    },
    [editor],
  );

  const onActionClick = useCallback(
    (actionName: EditorToolbarActions) => {
      if (TEXT_STYLES_ARRAY.includes(actionName as TextStyle)) {
        EditorActions.toggleMark(editor, actionName as TextStyle);
      }

      if (TEXT_INDENT_ARRAY.includes(actionName as TextIndent)) {
        EditorActions.indentText(editor, actionName as TextIndent);

        return;
      }

      if (ELEMENT_TYPES_ARRAY.includes(actionName as ElementTypes)) {
        EditorActions.toggleBlock(editor, actionName as ElementTypes);
      }
    },
    [editor],
  );

  const actionButtonProps = useMemo(
    () => ({
      disabled: readOnly,
      checkIfActionActive,
      onActionClick,
    }),
    [readOnly, checkIfActionActive, onActionClick],
  );

  return (
    <div className={classes}>
      <EditorActionButton actionName="bold" {...actionButtonProps}>
        <Bold />
      </EditorActionButton>
      <EditorActionButton actionName="italic" {...actionButtonProps}>
        <Italic />
      </EditorActionButton>
      <EditorActionButton actionName="underline" {...actionButtonProps}>
        <Underline />
      </EditorActionButton>
      <EditorActionButton actionName="ordered-list" {...actionButtonProps}>
        <OrderedList />
      </EditorActionButton>
      <EditorActionButton actionName="unordered-list" {...actionButtonProps}>
        <UnorderedList />
      </EditorActionButton>
      {additionalAction}
    </div>
  );
};

export default RichTextToolbar;
